// storage.js
/** @typedef {{recoverable: boolean, code: 'read-failed'|'corrupt-json'|'invalid-state'|'storage-unavailable'|'quota-exceeded'|'write-failed'|'remove-failed', operation: 'probe'|'read'|'write'|'remove', key: string|null, cause: unknown|null}} StorageIssue */

/** Versioned local persistence keys; `META` is intentionally unversioned. */
export const KEYS = Object.freeze({
  EX: 'completedExercises:v1',
  BLOCK: 'blockRounds:v1',
  EXR: 'completedExerciseRounds:v1',
  SOUND: 'wp:sound:v1',
  META: 'progressMeta',
});

export const STORAGE_VERSION = 1;

export class Storage {
  static _available = null;

  static _issues = new Map();

  static _lastIssue = null;

  static _corrupt = new Map();

  /**
   * Records a recoverable storage issue and emits a notification for a new signature.
   * @param {Omit<StorageIssue, 'recoverable'>|StorageIssue} issue
   * @returns {StorageIssue}
   */
  static reportIssue(issue) {
    // Reinsert duplicates to retain recency, while avoiding repetitive notifications.
    const normalized = { recoverable: true, ...issue };
    const { code, operation, key } = normalized;
    const issueId = `${code}:${operation}:${key}`;
    const isNew = !Storage._issues.has(issueId);
    if (!isNew) Storage._issues.delete(issueId);
    Storage._issues.set(issueId, normalized);
    Storage._lastIssue = normalized;
    if (
      isNew &&
      typeof document !== 'undefined' &&
      typeof document.dispatchEvent === 'function' &&
      typeof CustomEvent !== 'undefined'
    ) {
      document.dispatchEvent(new CustomEvent('storage:issue', { detail: normalized }));
    }
    return normalized;
  }

  /** Returns the most recently reported issue, if any. @returns {StorageIssue|null} */
  static getLastIssue() {
    return Storage._lastIssue;
  }

  /** Returns pending unique issues and clears the notification queue. @returns {StorageIssue[]} */
  static drainIssues() {
    const issues = Array.from(Storage._issues.values());
    Storage._issues.clear();
    return issues;
  }

  /** Clears recoverable availability issues after a successful retry. @returns {void} */
  static clearAvailabilityIssues() {
    const availabilityCodes = new Set(['storage-unavailable', 'quota-exceeded', 'write-failed']);
    Storage._issues.forEach((issue, issueId) => {
      if (availabilityCodes.has(issue.code)) Storage._issues.delete(issueId);
    });
    const remainingIssues = Array.from(Storage._issues.values());
    Storage._lastIssue = remainingIssues[remainingIssues.length - 1] ?? null;
  }

  /** Returns preserved raw values that could not be parsed or validated. @returns {Array<[string, string]>} */
  static getCorruptEntries() {
    return Array.from(Storage._corrupt.entries());
  }

  /** Preserves a corrupt raw value for recovery export. @param {string} key @param {string} raw @returns {void} */
  static preserveCorrupt(key, raw) {
    Storage._corrupt.set(key, raw);
  }

  /** Removes successfully replaced or discarded recovery entries. @param {string[]} keys @returns {void} */
  static clearCorrupt(keys) {
    keys.forEach((key) => Storage._corrupt.delete(key));
  }

  /** Reads an unparsed localStorage value without changing it on failure. @param {string} key @returns {string|null} */
  static getRaw(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      Storage.reportIssue({
        code: 'read-failed',
        operation: 'read',
        key,
        cause: e,
      });
      return null;
    }
  }

  /** Classifies a browser storage exception. @param {unknown} error @returns {'quota-exceeded'|'storage-unavailable'|'write-failed'} */
  static classifyError(error) {
    const isQuotaError = error?.name === 'QuotaExceededError' || error?.code === 22;
    if (isQuotaError) {
      // A quota-shaped error with an unreadable length is treated as unavailable.
      try {
        return localStorage.length > 0 ? 'quota-exceeded' : 'storage-unavailable';
      } catch {
        return 'storage-unavailable';
      }
    }

    return error?.name === 'SecurityError' ? 'storage-unavailable' : 'write-failed';
  }

  /** Probes and caches whether localStorage can safely persist values. @returns {boolean} */
  static isAvailable() {
    if (Storage._available !== null) return Storage._available;

    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      Storage._available = true;
    } catch (e) {
      // Cache an unavailable probe so repeated writes do not repeatedly throw.
      Storage._available = false;
      Storage.reportIssue({
        code: Storage.classifyError(e),
        operation: 'probe',
        key: null,
        cause: e,
      });
      console.warn('[Storage] localStorage not available:', e.message);
    }

    return Storage._available;
  }

  /** Re-probes storage and optionally announces successful recovery. @param {{notify?: boolean}} [options] @returns {boolean} */
  static retry({ notify = true } = {}) {
    const wasUnavailable = Storage._available === false;
    Storage._available = null;
    const available = Storage.isAvailable();

    if (wasUnavailable && available) {
      Storage.clearAvailabilityIssues();
      if (
        notify &&
        typeof document !== 'undefined' &&
        typeof document.dispatchEvent === 'function' &&
        typeof CustomEvent !== 'undefined'
      ) {
        document.dispatchEvent(new CustomEvent('storage:recovered'));
      }
    }

    return available;
  }

  /** Reads JSON while preserving an unreadable raw value for later recovery. @param {string} key @param {unknown} fallback @returns {unknown} */
  static getJSON(key, fallback) {
    const v = Storage.getRaw(key);
    if (v === null) return fallback;

    try {
      return JSON.parse(v);
    } catch (e) {
      Storage.preserveCorrupt(key, v);
      Storage.reportIssue({
        code: 'corrupt-json',
        operation: 'read',
        key,
        cause: e,
      });
      return fallback;
    }
  }

  /** Writes a JSON value, returning false rather than throwing for recoverable failures. @param {string} key @param {unknown} value @returns {boolean} */
  static setJSON(key, value) {
    if (!Storage.isAvailable()) {
      Storage.reportIssue({
        code: 'storage-unavailable',
        operation: 'write',
        key,
        cause: null,
      });
      console.warn('[Storage] Cannot save - localStorage not available');
      return false;
    }
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (e) {
      // Keep quota diagnostics distinct from other write failures for recovery UI.
      if (e.name === 'QuotaExceededError' || e.code === 22) {
        console.warn('[Storage] Quota exceeded for key:', key);
      } else {
        console.warn('[Storage] Set failed:', key, e);
      }
      Storage.reportIssue({
        code: Storage.classifyError(e),
        operation: 'write',
        key,
        cause: e,
      });
      return false;
    }
  }

  /** Removes a key and its recovery copy after a successful browser deletion. @param {string} key @param {boolean} [shouldWarn=true] @returns {boolean} */
  static remove(key, shouldWarn = true) {
    if (!Storage.isAvailable()) {
      Storage.reportIssue({
        code: 'remove-failed',
        operation: 'remove',
        key,
        cause: new Error('localStorage unavailable'),
      });
      return false;
    }
    try {
      localStorage.removeItem(key);
      Storage.clearCorrupt([key]);
      return true;
    } catch (e) {
      Storage.reportIssue({
        code: 'remove-failed',
        operation: 'remove',
        key,
        cause: e,
      });
      if (shouldWarn) console.warn('[Storage] Remove failed:', key, e);
      return false;
    }
  }

  /** Writes metadata only when its stored schema version differs. @returns {void} */
  static ensureVersion() {
    if (!Storage.isAvailable()) return;
    try {
      const meta = Storage.getJSON(KEYS.META, {});
      if (meta.version !== STORAGE_VERSION) {
        Storage.setJSON(KEYS.META, {
          version: STORAGE_VERSION,
          migratedAt: new Date().toISOString(),
        });
      }
    } catch (e) {
      console.warn('[Storage] Could not ensure storage version', e);
    }
  }

  /** Attempts to remove every known application key. @returns {boolean} */
  static clear() {
    const results = Object.values(KEYS).map((key) => Storage.remove(key, false));
    if (results.every(Boolean)) return true;

    console.warn('[Storage] Clear failed:', Storage._lastIssue?.cause);
    return false;
  }
}
